/**
 * Application bootstrap.
 *
 * Hash routes:
 *   #/                → estate listing (default landing, anonymous OK)
 *   #/estates/:id     → estate detail + valuation (anon → register prompt)
 *   #/my-estates      → "Omat tilat" placeholder (M5)
 *   #/market          → "Markkinayhteenveto" placeholder (M6)
 *   #/admin/users     → user management (admin only, M2 endpoints)
 *
 * The header (`refreshHeader`) hides nav buttons that the current user
 * cannot use, so unauthenticated visitors never see "Käyttäjähallinta" but
 * the route is still defended by the admin endpoint's middleware in case
 * someone deep-links to it.
 */
import './styles/mockup.css';
import './styles/app.css';

import { onUnauthorized } from './api/client.ts';
import { refreshHeader, bindLogout } from './views/header.ts';
import { currentUser, isRegionScopedWithoutRegions } from './state/session.ts';
import { bindAuthModals, setAfterLogin, hideAllOverlays } from './views/auth-modals.ts';
import { bindListing, load as loadListing, getLoadedRows } from './views/listing.ts';
import { openEstate } from './views/estate-detail.ts';
import { openAdminPanel } from './views/admin-panel.ts';
import { renderMyEstates } from './views/my-estates.ts';
import { renderMarket } from './views/market.ts';
import { renderSellerSubmissionForm } from './views/seller-submission-form.ts';
import { bindSearch } from './views/search.ts';
import { bindFooterModals } from './views/footer-modals.ts';
import { applyToDOM, bindLangToggle, getLang } from './i18n/index.ts';
import { bindThemeToggle } from './state/theme.ts';

function go(hash: string): void {
  if (window.location.hash !== hash) {
    window.location.hash = hash;
  } else {
    void render();
  }
}

type AdminTab = 'users' | 'create' | 'shares' | 'orders' | 'feedback' | 'log';

type Route =
  | { name: 'listing' }
  | { name: 'detail'; id: number }
  | { name: 'my-estates' }
  | { name: 'market' }
  | { name: 'seller-submission' }
  | { name: 'admin'; tab: AdminTab };

function parseHash(): Route {
  const h = window.location.hash.replace(/^#/, '');
  let m: RegExpMatchArray | null;
  if ((m = h.match(/^\/estates\/(\d+)$/))) return { name: 'detail', id: Number(m[1]) };
  if (h === '/my-estates') return { name: 'my-estates' };
  if (h === '/market') return { name: 'market' };
  if (h.startsWith('/myynti-ilmoitus')) return { name: 'seller-submission' };
  if (h === '/admin/users' || h === '/admin') return { name: 'admin', tab: 'users' };
  if (h === '/admin/create') return { name: 'admin', tab: 'create' };
  if (h === '/admin/shares') return { name: 'admin', tab: 'shares' };
  if (h === '/admin/orders') return { name: 'admin', tab: 'orders' };
  if (h === '/admin/feedback') return { name: 'admin', tab: 'feedback' };
  if (h === '/admin/audit' || h === '/admin/log') return { name: 'admin', tab: 'log' };
  return { name: 'listing' };
}

/** True when the route renders into `#targetSection` and should hide the
 *  default search + listing sections. The admin route is special: it opens
 *  a modal overlay and keeps the listing visible behind it. */
function isOverlayRoute(r: Route): boolean {
  return r.name !== 'listing' && r.name !== 'admin';
}

async function render(): Promise<void> {
  refreshHeader();
  const r = parseHash();

  // Level=1 users without any allowed_regions cannot use the listing (the
  // backend rejects it with "no allowed_regions configured"), so send them
  // to "Omat tilat" instead of showing the error (asiakaspalaute 12.6.2026).
  if (r.name === 'listing' && isRegionScopedWithoutRegions(currentUser())) {
    go('#/my-estates');
    return;
  }

  const listing = document.getElementById('listingSection')!;
  const search = document.querySelector<HTMLElement>('.search-section');
  const target = document.getElementById('targetSection')!;

  if (isOverlayRoute(r)) {
    listing.style.display = 'none';
    if (search) search.style.display = 'none';
  } else {
    listing.style.display = '';
    if (search) search.style.display = '';
    target.innerHTML = '';
  }

  switch (r.name) {
    case 'listing':
      await loadListing();
      break;
    case 'detail':
      await openEstate(r.id, { onBack: () => go('#/') });
      break;
    case 'my-estates':
      await renderMyEstates(target);
      break;
    case 'market':
      await renderMarket(target);
      break;
    case 'seller-submission':
      await renderSellerSubmissionForm(target);
      break;
    case 'admin':
      // Admin panel is a modal — open it on top of whatever was rendered.
      // The listing in the background still loads so closing the modal
      // (or navigating away) reveals the previous state.
      await loadListing();
      openAdminPanel(r.tab);
      break;
  }
}

window.addEventListener('hashchange', () => void render());

// Apply i18n + theme on initial load, then wire the toggles.
document.documentElement.setAttribute('lang', getLang());
applyToDOM();
bindLangToggle();
bindThemeToggle();

// Wire static event handlers once.
bindAuthModals();
bindListing({ onSelect: (id) => go(`#/estates/${id}`) });
bindSearch({
  rows: getLoadedRows,
  onSelect: (id) => go(`#/estates/${id}`),
});
bindFooterModals();
bindLogout(() => {
  refreshHeader();
  go('#/');
});

// Header navigation buttons.
document.getElementById('headerBrand')!.addEventListener('click', () => go('#/'));
document.getElementById('headerBrand')!.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    go('#/');
  }
});
document.getElementById('myEstatesBtn')!.addEventListener('click', () => go('#/my-estates'));
document.getElementById('institutionBtn')!.addEventListener('click', () => go('#/market'));
document.getElementById('adminMenuBtn')!.addEventListener('click', () => go('#/admin'));
// Legacy header buttons are hidden in M6.2 but bound here for resilience
// in case a stale shortcut still points to them.
document.getElementById('adminOrdersBtn')?.addEventListener('click', () => go('#/admin/orders'));
document.getElementById('adminFeedbackBtn')?.addEventListener('click', () => go('#/admin/feedback'));
document.getElementById('adminAuditBtn')?.addEventListener('click', () => go('#/admin/log'));

// Language switch → re-render dynamic content (table headers, valuation labels).
// We intentionally skip the admin route here: the admin panel is a modal that
// the user may have already closed while the hash still points to #/admin/*.
// Calling render() in that state would silently reopen the modal. The static
// [data-i18n] elements inside the modal are already updated by applyToDOM()
// which runs synchronously inside setLang() before this event fires.
window.addEventListener('smt:lang', () => {
  if (parseHash().name !== 'admin') void render();
});

// After login: close modals, refresh header, reload current view.
setAfterLogin(() => {
  hideAllOverlays();
  refreshHeader();
  void render();
});

// Wipe-on-401 → bounce back to anonymous listing.
onUnauthorized(() => go('#/'));

// Initial render.
void render();
